import React, { FC, useCallback, useEffect } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import classes from './ActivationView.module.less';
import { isTokenValid } from '../../../common/validators';
import {
  getActivationError, getActivationErrorText, getIsActivatingAccount, getIsActivationLinkRejected,
} from '../slice/selectors';
import logo from '../../../assets/ logo.png';
import useGoToRoute from '../../../hooks/useGoToRoute';
import { Path } from '../../../constants/Path';
import Text from '../../../components/Text';
import { activateAccount } from '../slice/actions';
import { error, success } from '../../../components/Message';
import useActionStatus from '../../../hooks/useActionStatus';

const ActivationView: FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const goToLogin = useGoToRoute(Path.LOGIN);

  const errorMessage = useSelector(getActivationError);
  const errorText = useSelector(getActivationErrorText);
  const isLinkRejected = useSelector(getIsActivationLinkRejected);
  // Wygasły albo nieczytelny link front rozpoznaje sam, bez pytania API
  const isLinkValid = isTokenValid(token);

  const onSuccess = useCallback(() => {
    success(t('common:signUp.messages.activationSuccess'));
    goToLogin();
  }, [t]);

  // Po błędzie zostajemy na stronie: zamiast kółka widać powód i przejście do logowania
  const onError = useCallback(() => {
    error(t('common:signUp.messages.activationError'));
  }, [t]);

  useActionStatus(onSuccess, onError, {
    isLoadingSelector: getIsActivatingAccount,
    errorSelector: getActivationError,
  });

  useEffect(() => {
    if (isTokenValid(token)) {
      dispatch(activateAccount(token));
    }
  }, [dispatch, token]);

  const hasFailed = !isLinkValid || Boolean(errorMessage);
  const failureText = isLinkValid
    ? errorText ?? t(isLinkRejected ? 'common:activation.messages.invalidLinkTeacher' : 'common:activation.messages.genericError')
    : t('common:activation.messages.invalidLinkTeacher');

  return (
    <div className={classes.container}>
      <img className={classes.logo} src={logo} alt="Logo" />
      <div />
      {hasFailed ? (
        <>
          <Text className={classes.message}>{failureText}</Text>
          <Button size="large" onClick={goToLogin} className={classes.backButton} style={{ marginTop: 24 }}>
            {t('common:activation.labels.goToLogin')}
          </Button>
        </>
      ) : (
        <LoadingOutlined style={{ fontSize: '60px' }} />
      )}
    </div>
  );
};

export default ActivationView;
